/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { Link } from '@/components/link';
import { ResponsiveBannerImage } from '@/components/responsive-banner-image';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

/** Full width when stacked on mobile; the narrow (30%) Split Layout column from md up. */
const CTA_CARD_IMAGE_WIDTHS = { base: '100vw', md: '30vw' };

@Component('ctaCard', {
    name: 'CTA Card',
    group: 'priceshoes_content',
    description:
        'Promo card, e.g. next to the "Ofertas del mes" carousel: background image with a text and a full-width button at the bottom. Only the button navigates.',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components -- oxlint flags the co-exported Page Designer metadata class; eslint-plugin-react-refresh does not
export class CtaCardMetadata {
    @AttributeDefinition({ name: 'Image', type: 'image', required: true, description: 'Background image (desktop)' })
    image!: string;

    @AttributeDefinition({
        name: 'Mobile Image',
        type: 'image',
        required: false,
        description: 'Background image for mobile. Falls back to the desktop image when empty.',
    })
    mobileImage?: string;

    @AttributeDefinition({ name: 'Image Alt Text', type: 'string', required: false })
    imageAlt?: string;

    @AttributeDefinition({ name: 'Text', type: 'string', required: false, description: 'e.g. "20% DE DESCUENTO"' })
    title?: string;

    @AttributeDefinition({ name: 'Button Text', type: 'string', required: false, description: 'e.g. "Ver más"' })
    ctaText?: string;

    @AttributeDefinition({
        name: 'Button Link',
        type: 'url',
        required: false,
        description: 'The button is shown only when both text and link are set',
    })
    ctaLink?: string;
}

interface CtaCardProps {
    image?: Image;
    mobileImage?: Image;
    imageAlt?: string;
    title?: string;
    ctaText?: string;
    ctaLink?: string;
    /** Set by a parent Carousel: only its first item is the LCP candidate. */
    priority?: 'high' | 'auto';
    loading?: 'eager' | 'lazy';
    className?: string;
}

/**
 * PriceShoes CTA card (PRIC-58 CA2): background image with a bottom panel holding the text and a
 * full-width "Ver más" button. The card itself is not a link; only the button navigates. It fills
 * the height of its container (e.g. matches the carousel next to it in a Split Layout).
 */
export default function CtaCard({
    image,
    mobileImage,
    imageAlt,
    title,
    ctaText,
    ctaLink,
    priority = 'auto',
    loading = 'lazy',
    className,
}: CtaCardProps): ReactElement | null {
    const { t } = useTranslation('common');
    const { isDesignMode } = usePageDesignerMode();

    const heading = title?.trim();
    const showCta = Boolean(ctaText?.trim() && ctaLink);
    const isEmpty = !image?.url && !mobileImage?.url && !heading && !showCta;

    if (isEmpty && !isDesignMode) return null;

    return (
        <div
            data-slot="cta-card"
            className={cn(
                'relative flex h-full min-h-[320px] w-full flex-col justify-end overflow-hidden rounded-2xl bg-ps-gray-200 shadow-[0_2px_23px_rgb(0_0_0/0.07)]',
                className
            )}>
            <ResponsiveBannerImage
                image={image}
                mobileImage={mobileImage}
                alt={imageAlt}
                widths={CTA_CARD_IMAGE_WIDTHS}
                priority={priority}
                loading={loading}
                className="absolute inset-0 size-full"
            />

            {(heading || showCta || isEmpty) && (
                // Text and colors are fixed by the design (Figma), not configurable.
                <div className="relative flex flex-col gap-4 bg-ps-primary-static/16 p-6">
                    {(heading || isEmpty) && (
                        <h3 className="text-[28px] font-bold leading-[48px] text-ps-white">
                            {heading || t('ctaCard.emptyTitle')}
                        </h3>
                    )}
                    {showCta && (
                        <Link
                            to={ctaLink as string}
                            className="flex w-full items-center justify-center rounded-lg bg-ps-white px-[22px] py-[11px] text-base font-bold leading-6 text-ps-accent transition-colors hover:bg-ps-gray-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ps-focus">
                            {ctaText}
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
}
